import React from 'react';
import { title } from '../config/config';
import SEO, { seoConfig } from '../component/SEO';
import PageShapes from '../component/PageShapes';

const Privacy = () => {
  return (
    <>
      <SEO {...seoConfig.privacy} />
      <PageShapes />
      <div className="legal-page">
        <header className="legal-header">
          <div className="badge">Safe & Secure</div>
          <h1>Privacy <span>Policy</span></h1>
          <p>How we handle your data and protect your ideas — Last updated: April 2026</p>
        </header>

        <article className="legal-content">

          <section>
            <h3>1. Who We Are</h3>
            <p>
              {title} is an idea-sharing platform where creators, builders, and thinkers can submit,
              explore, and discuss ideas and problems. We are committed to keeping your experience
              private, safe, and transparent. This policy explains what information we collect,
              how we use it, and what rights you have over it.
            </p>
          </section>

          <section>
            <h3>2. What Information We Collect</h3>
            <p>We collect only what is necessary to provide you with a great experience:</p>
            <ul className="legal-list">
              <li><strong>Account Information:</strong> When you register, we collect your name and email address to create and manage your account.</li>
              <li><strong>Profile Data:</strong> Your display name and any optional profile details you choose to add.</li>
              <li><strong>Content You Submit:</strong> Ideas, problems, comments, and any other content you post on the platform.</li>
              <li><strong>Usage Data:</strong> Basic information about how you interact with the platform — pages visited, features used — to help us improve the experience.</li>
              <li><strong>Device & Technical Data:</strong> Browser type, device type, and IP address for security and performance purposes.</li>
            </ul>
          </section>

          <section>
            <h3>3. How We Use Your Information</h3>
            <p>Your data is used solely to operate and improve {title}. Specifically:</p>
            <ul className="legal-list">
              <li>To create and manage your account securely.</li>
              <li>To display your submitted ideas and comments on the platform.</li>
              <li>To send important account-related notifications (not marketing spam).</li>
              <li>To detect and prevent abuse, spam, or unauthorized access.</li>
              <li>To analyze platform usage and improve features over time.</li>
              <li>To respond to your support requests or feedback.</li>
            </ul>
          </section>

          <section>
            <h3>4. Your Content & Ownership</h3>
            <p>
              Everything you create on {title} belongs to you. We do not claim ownership over
              your ideas, problems, or any content you submit. When you post publicly, you grant
              {title} a limited license to display that content on the platform. You can delete
              your content at any time, and it will be removed from public view immediately.
            </p>
          </section>

          <section>
            <h3>5. Public vs Private Content</h3>
            <p>
              {title} gives you control over the visibility of your submissions. Content marked
              as <strong>Public</strong> is visible to all visitors. Content marked as <strong>Private</strong> is
              only visible to you when logged in. We respect this setting and do not expose
              private content to other users or third parties.
            </p>
          </section>

          <section>
            <h3>6. Cookies</h3>
            <p>
              We use essential cookies to keep you logged in and maintain your session. We do not
              use advertising cookies or tracking pixels. You can clear cookies at any time through
              your browser settings, though this will log you out of your account.
            </p>
          </section>

          <section>
            <h3>7. Data Sharing</h3>
            <p>
              We do not sell, rent, or trade your personal information to anyone. We do not share
              your data with advertisers or data brokers. Your information stays within the
              {title} platform and is used only to provide you with the service.
            </p>
          </section>

          <section>
            <h3>8. Data Security</h3>
            <p>
              We take security seriously. Your account is protected with secure authentication,
              and all data is transmitted over encrypted connections (HTTPS). We regularly review
              our security practices to ensure your information stays protected. In the unlikely
              event of a data breach, we will notify affected users promptly.
            </p>
          </section>

          <section>
            <h3>9. How Long We Keep Your Data</h3>
            <p>
              We retain your account data for as long as your account is active. If you delete
              your account, your personal information and private content will be permanently
              removed from our systems within 30 days. Public content you have posted may remain
              visible unless you delete it before closing your account.
            </p>
          </section>

          <section>
            <h3>10. Your Rights</h3>
            <p>You have full control over your data. At any time, you can:</p>
            <ul className="legal-list">
              <li><strong>Access:</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Edit:</strong> Update your profile and account information directly from your dashboard.</li>
              <li><strong>Delete:</strong> Remove your content or close your account entirely.</li>
              <li><strong>Object:</strong> Opt out of any non-essential data processing.</li>
              <li><strong>Portability:</strong> Request your data in a portable format.</li>
            </ul>
          </section>

          <section>
            <h3>11. Children's Privacy</h3>
            <p>
              {title} is intended for users aged 13 and above. We do not knowingly collect
              personal information from children under 13. If we become aware that a child under
              13 has created an account, we will delete the account and associated data immediately.
            </p>
          </section>

          <section>
            <h3>12. Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time as the platform evolves. When
              we make significant changes, we will notify you through the platform or via email.
              The date at the top of this page always reflects the most recent update. Continued
              use of {title} after changes means you accept the updated policy.
            </p>
          </section>

          <section className="contact-box">
            <h3>Questions or Concerns?</h3>
            <p>
              If you have any questions about this Privacy Policy or how your data is handled,
              feel free to reach out to us. We are happy to help.
            </p>
            <div className="email-highlight">thedc.here@gmail.com</div>
          </section>

        </article>
      </div>
    </>
  );
};

export default Privacy;
